import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Message, Suggestion } from '../types';
import { chatAPI } from '../services/api';
import { storage } from '../utils/storage';
import ChatMessage from '../components/ChatMessage';
import SuggestionPills from '../components/SuggestionPills';
import ChatInput from '../components/ChatInput';
import WelcomeScreen from '../components/WelcomeScreen';
import FollowUpPill from '../components/FollowUpPill';

function ChatPage() {
  const [messages, setMessages] = useState<Message[]>([]);
  const [currentSuggestions, setCurrentSuggestions] = useState<Suggestion[]>([]);
  const [previousSuggestions, setPreviousSuggestions] = useState<Suggestion[]>([]);
  const [currentFollowUp, setCurrentFollowUp] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const sessionIdRef = useRef<string>(storage.getSessionId());
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  // Load chat history on mount
  useEffect(() => {
    const savedHistory = storage.getChatHistory();
    if (savedHistory.length > 0) {
      setMessages(savedHistory);
    }
  }, []);

  // Save chat history whenever messages change
  useEffect(() => {
    if (messages.length > 0) {
      storage.setChatHistory(messages);
    }
  }, [messages]);

  const handleSendMessage = async (messageText: string) => {
    if (!messageText.trim() || isLoading) return;

    const userMessage: Message = {
      id: Date.now().toString(),
      type: 'user',
      content: messageText,
      timestamp: new Date(),
    };

    setMessages((prev) => [...prev, userMessage]);
    setCurrentFollowUp(null);

    setPreviousSuggestions(currentSuggestions);
    setIsLoading(true);
    setError(null);

    try {
      const response = await chatAPI.sendMessage({
        message: messageText,
        user_profile: storage.getUserProfile(),
        session_id: sessionIdRef.current,
      });

      const assistantMessage: Message = {
        id: (Date.now() + 1).toString(),
        type: 'assistant',
        content: response.answer,
        timestamp: new Date(),
        sources: response.sources,
        suggestions: response.suggestions,
        followUp: response.follow_up,
        uiActions: response.ui_actions,
      };

      setMessages((prev) => [...prev, assistantMessage]);
      setCurrentSuggestions(response.suggestions || []);
      setCurrentFollowUp(response.follow_up || null);

      // Handle navigation UI actions
      if (response.ui_actions?.navigate_to && response.ui_actions.navigate_to !== 'none') {
        setTimeout(() => {
          if (response.ui_actions?.navigate_to === 'degree_plan') {
            navigate('/degree-plan');
          } else if (response.ui_actions?.navigate_to === 'events') {
            navigate('/events');
          }
        }, 1000);
      }
    } catch (err) {
      console.error('Error sending message:', err);
      setError('Failed to send message. Please check your connection and try again.');
      setCurrentSuggestions(previousSuggestions);
    } finally {
      setIsLoading(false);
    }
  };

  const handleSuggestionClick = (prompt: string) => {
    handleSendMessage(prompt);
  };

  const showWelcome = messages.length === 0 && !isLoading;

  return (
    <div className="flex flex-col h-[calc(100vh-64px)] bg-gray-50">
      <div className="flex-1 overflow-y-auto px-4 py-6">
        <div className="max-w-4xl mx-auto">
          {showWelcome && <WelcomeScreen onQuickQuestion={handleSendMessage} />}

          {messages.map((message) => (
            <ChatMessage key={message.id} message={message} />
          ))}

          {isLoading && (
            <div className="flex items-start space-x-3 mb-6">
              <div className="w-8 h-8 rounded-full bg-[#C41230] flex items-center justify-center text-white font-bold shrink-0">
                AI
              </div>
              <div className="bg-white rounded-2xl rounded-tl-none px-6 py-4 shadow-sm max-w-3xl">
                <div className="flex items-center space-x-2">
                  <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce"></div>
                  <div
                    className="w-2 h-2 bg-gray-400 rounded-full animate-bounce"
                    style={{ animationDelay: '0.2s' }}
                  ></div>
                  <div
                    className="w-2 h-2 bg-gray-400 rounded-full animate-bounce"
                    style={{ animationDelay: '0.4s' }}
                  ></div>
                </div>
              </div>
            </div>
          )}

          {error && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-4">
              <p className="text-red-600 font-semibold">{error}</p>
            </div>
          )}

          <div ref={messagesEndRef} />
        </div>
      </div>

      {/* Sticky bottom section */}
      <div className="bg-white border-t border-gray-200 px-4 py-4">
        <div className="max-w-4xl mx-auto">
          {(currentSuggestions.length > 0 || (isLoading && previousSuggestions.length > 0)) && (
            <div className="mb-3">
              <SuggestionPills
                suggestions={isLoading ? previousSuggestions : currentSuggestions}
                onSuggestionClick={handleSuggestionClick}
              />
            </div>
          )}

          {currentFollowUp && !isLoading && (
            <div className="mb-3">
              <FollowUpPill
                question={currentFollowUp}
                onFollowUpClick={handleSendMessage}
              />
            </div>
          )}

          <ChatInput onSendMessage={handleSendMessage} disabled={isLoading} />
        </div>
      </div>
    </div>
  );
}

export default ChatPage;
